import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../../utils/api';
import ItemEditor from '../checklists/ItemEditor';
import { normalizeChecklistItemOptions } from '../../utils/checklistItemUtils';

export interface LogTemplateField {
  id?: number;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: Record<string, unknown>;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

export interface LogTemplate {
  id: number;
  title: string;
  description: string;
  category: string;
  version: string;
  is_current_version: boolean;
  is_active: boolean;
  fields?: LogTemplateField[];
}

interface LogTemplateBuilderProps {
  open: boolean;
  onClose: () => void;
  template?: LogTemplate | null;
}

const categoryOptions = [
  { value: 'safety', label: 'Safety' },
  { value: 'injury', label: 'Injury Incident' },
  { value: 'workplace_violence', label: 'Workplace Violence' },
  { value: 'near_miss', label: 'Near-Miss' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'other', label: 'Other' },
];

const itemTypeOptions = [
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'text_input', label: 'Text Input' },
  { value: 'number_input', label: 'Number Input' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'date_input', label: 'Date Input' },
  { value: 'time_input', label: 'Time Input' },
  { value: 'select', label: 'Dropdown Selection' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'signature', label: 'Digital Signature' },
  { value: 'location', label: 'Location/GPS' },
  { value: 'photo', label: 'Photo Capture' },
];

const LogTemplateBuilder: React.FC<LogTemplateBuilderProps> = ({ open, onClose, template }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<LogTemplateField[]>([]);
  const [editingField, setEditingField] = useState<LogTemplateField | null>(null);
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'other',
  });

  useEffect(() => {
    if (!open) return;
    if (template) {
      setForm({
        title: template.title,
        description: template.description || '',
        category: template.category || 'other',
      });
      setFields(
        (template.fields || []).map((field) => ({
          ...field,
          options: normalizeChecklistItemOptions(field.options),
        })),
      );
    } else {
      setForm({ title: '', description: '', category: 'other' });
      setFields([]);
    }
    setError(null);
  }, [open, template]);

  const serializeFields = () =>
    fields.map((field, index) => ({
      title: field.title,
      description: field.description,
      item_type: field.item_type,
      order: index + 1,
      is_required: field.is_required,
      validation_rules: field.validation_rules || {},
      options: field.options || [],
      default_value: field.default_value || '',
      allowed_file_types: field.allowed_file_types || '',
      max_file_size_mb: field.max_file_size_mb || 10,
      is_visible: field.is_visible !== false,
      show_when_parent_completed: field.show_when_parent_completed || false,
    }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (fields.length === 0) {
      setError('Add at least one field before saving this log template.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        category: form.category,
        is_active: true,
        fields: serializeFields(),
      };

      if (template?.id) {
        await api.patch(`/log-templates/${template.id}/`, payload);
      } else {
        await api.post('/log-templates/', payload);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to save log template');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    setEditingField({
      title: '',
      description: '',
      item_type: 'text_input',
      order: fields.length + 1,
      is_required: true,
      validation_rules: {},
      options: [],
      default_value: '',
      allowed_file_types: '',
      max_file_size_mb: 10,
      is_visible: true,
      show_when_parent_completed: false,
    });
    setFieldEditorOpen(true);
  };

  const handleEditField = (field: LogTemplateField) => {
    setEditingField({ ...field, options: normalizeChecklistItemOptions(field.options) });
    setFieldEditorOpen(true);
  };

  const handleSaveField = (item: any) => {
    const field: LogTemplateField = {
      ...item,
      options: normalizeChecklistItemOptions(item.options),
    };
    const existingIndex = field.id
      ? fields.findIndex((f) => f.id === field.id)
      : fields.findIndex((f) => !f.id && f.title === field.title && f.order === field.order);

    if (existingIndex >= 0) {
      setFields((prev) => prev.map((f, i) => (i === existingIndex ? field : f)));
    } else {
      setFields((prev) => [...prev, field]);
    }
    setFieldEditorOpen(false);
    setEditingField(null);
  };

  const handleDeleteField = (index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{template ? 'Edit Log Template' : 'New Log Template'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category}
                  label="Category"
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
            <Typography variant="h6">Fields</Typography>
            <Button startIcon={<AddIcon />} onClick={handleAddField}>Add Field</Button>
          </Box>

          {fields.length === 0 ? (
            <Alert severity="info">Add at least one field for this log template.</Alert>
          ) : (
            <List dense>
              {fields.map((field, index) => (
                <ListItem key={field.id || `new-${index}`} divider>
                  <ListItemText
                    primary={`${index + 1}. ${field.title}`}
                    secondary={`${field.item_type}${field.is_required ? ' • required' : ''}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleEditField(field)} aria-label="Edit field">
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleDeleteField(index)} aria-label="Delete field">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      <ItemEditor
        open={fieldEditorOpen}
        onClose={() => {
          setFieldEditorOpen(false);
          setEditingField(null);
        }}
        item={editingField as any}
        onSave={handleSaveField}
        itemTypeOptions={itemTypeOptions}
      />
    </>
  );
};

export default LogTemplateBuilder;
